import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore, getRecoveryToken } from '../../stores/authStore';
import { AIStatusBadge } from '../ui/AIStatusBadge';
import { getBugReportConfig } from '../../utils/bugReportLink';

export function Header() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthStore();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  // Check if this is a local user by checking for recovery token
  const recoveryToken = getRecoveryToken();
  const isLocalUser = !!recoveryToken;

  const handleLogoutClick = () => {
    if (isLocalUser) {
      // Show warning dialog for local users
      setShowLogoutDialog(true);
    } else {
      // Direct logout for GitHub users
      logout();
      navigate('/login', { replace: true });
    }
  };

  const handleConfirmLogout = () => {
    setShowLogoutDialog(false);
    logout();
    navigate('/login', { replace: true });
  };

  const handleCopyToken = async () => {
    if (recoveryToken) {
      try {
        await navigator.clipboard.writeText(recoveryToken);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <>
      <header className="border-b border-border bg-background">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-primary">
            Veodiagram
          </Link>

          <nav className="flex items-center gap-4">
            <AIStatusBadge />
            <BugReportLink />
            {isAuthenticated ? (
              <>
                <Link
                  to="/dashboard"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Dashboard
                </Link>
                <Link
                  to="/settings"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Settings
                </Link>
                <div className="flex items-center gap-3">
                  {user?.avatarUrl && (
                    <img
                      src={user.avatarUrl}
                      alt={user.name || 'User'}
                      className="w-8 h-8 rounded-full"
                    />
                  )}
                  <span className="text-sm">{user?.name || user?.email}</span>
                  <button
                    onClick={handleLogoutClick}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <Link
                to="/login"
                className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Sign in with GitHub
              </Link>
            )}
          </nav>
        </div>
      </header>

      {/* Logout Warning Dialog for Local Users */}
      {showLogoutDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background border border-border rounded-lg shadow-lg max-w-md w-full mx-4 p-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">
              Warning: You may lose access to your data
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              As a local user, logging out will disconnect you from this account.
              Without your recovery token, you won't be able to access your projects
              and diagrams again.
            </p>

            <div className="mb-4">
              <label className="text-sm font-medium text-foreground block mb-2">
                Your Recovery Token (save this somewhere safe):
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={recoveryToken || ''}
                  className="flex-1 px-3 py-2 text-xs font-mono bg-muted border border-border rounded-md"
                />
                <button
                  onClick={handleCopyToken}
                  className="px-3 py-2 text-sm bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors"
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                You can use this token to recover your account later via the settings page.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowLogoutDialog(false)}
                className="px-4 py-2 text-sm bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmLogout}
                className="px-4 py-2 text-sm bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors"
              >
                Logout Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * #969: Persistent "Report a bug" link in the global header. Opens
 * the GitHub issue chooser in a new tab. Hidden when
 * VITE_BUG_REPORT_DISABLED=true. The link target is a plain URL
 * with no client-side context — the GitHub issue form scaffolds the
 * report.
 */
export function BugReportLink({ inline = false }: { inline?: boolean }) {
  const { enabled, href } = getBugReportConfig();
  if (!enabled) return null;
  const className = inline
    ? 'text-sm text-blue-600 dark:text-blue-400 hover:underline'
    : 'text-sm text-muted-foreground hover:text-foreground transition-colors';
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      data-testid="bug-report-link"
      title="Report a bug on GitHub (opens in new tab)"
    >
      Report a bug
    </a>
  );
}
